# MatchingGame.vue
<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, inject } from 'vue'
import { useI18n } from 'vue-i18n'
import { getBaseUrl } from '@/helpers/fetch-wrapper'
import { useAuthStore } from '@/stores/auth.store'

const { t } = useI18n()
const { user } = useAuthStore()
const audiomanager = inject('audiomanager')
const registerScrollHandler = inject('registerScrollHandler')
const svgElement = ref(null)

const props = defineProps({
  gameState: {
    type: Object,
    required: true
  },
  enable_audio: {
    type: Boolean,
  },
  compact: {
    type: Boolean,
    default: false
  },
})

const emit = defineEmits(['game-action', 'toggle-audio'])

// Local UI state
const selectedPair = ref(null)
const wordPositions = ref({})
const matchedWordPositions = ref([]) // Changed from computed to ref

// Computed properties
console.log("ok making computed variable based on props.gameState.pairs", props.gameState.pairs)
const pairs = computed(() => {
  return props.gameState.pairs
})

const nativeWords = ref([])
const learningWords = ref([])

console.log("gameState.pairs is", props.gameState.pairs)

nativeWords.value = props.gameState.pairs
  .map(pair => ({
    id: pair.id,
    text: pair.native,
    type: 'native',
    matched: pair.matched
  }))
  .sort(() => Math.random() - 0.5)

learningWords.value = props.gameState.pairs
  .map(pair => ({
    id: pair.id,
    text: pair.learning,
    type: 'learning',
    pronunciation: pair.pronunciation,
    annotation: pair.annotated.annotation,
    matched: pair.matched
  }))
  .sort(() => Math.random() - 0.5)


// Methods
function updateMatchedWordPositions() {
  console.log("updateMatchedWordPositions running")
  matchedWordPositions.value = nativeWords.value
    .filter(pair => pair.matched)
    .map(pair => ({
      native: wordPositions.value[`native-${pair.id}`],
      learning: wordPositions.value[`learning-${pair.id}`]
    }))
  console.log("matchedWordPositions is", matchedWordPositions.value)
}

async function selectWord(word) {
  if (word.matched) {
    console.log("word is already matched");

    // Play audio for the learning language item
    if (word.type === 'learning') {
      playAudio(word.pronunciation);
    } else {
      // Find the corresponding learning word and play its audio
      const learningWord = learningWords.value.find(w => w.id === word.id);
      if (learningWord) {
        playAudio(learningWord.pronunciation);
      }
    }
    return;
  }

  updateWordPositions()
  if (!selectedPair.value) {
    selectedPair.value = word
    return
  }

  // If clicking same word, deselect it
  if (selectedPair.value.id === word.id && selectedPair.value.type === word.type) {
    selectedPair.value = null
    return
  }

  // If selecting same type (native/learning), switch selection
  if (selectedPair.value.type === word.type) {
    selectedPair.value = word
    return
  }

  // Check if pair matches
  const isMatch = selectedPair.value.id === word.id
  if (isMatch) {
    console.log("detecting match")
    selectedPair.value.matched = true
    word.matched = true
    console.log("now state is", selectedPair.value)
    updateMatchedWordPositions() // Update matchedWordPositions when a match is found

    // Play audio for the learning language option
    console.log("i am here")
    if (selectedPair.value.type === 'native') {
      console.log("word is", word)
      playAudio(word.pronunciation)
    } else {
      console.log("selectedPair.value is", selectedPair.value)
      playAudio(selectedPair.value.pronunciation)
    }
  } else {
    // Add wrong class to both words
    const savedSelectedPair = selectedPair.value
    savedSelectedPair.wrong = true
    word.wrong = true

    // Remove wrong class after 1 second
    setTimeout(() => {
      savedSelectedPair.wrong = false
      word.wrong = false
    }, 1000)
  }

  const allMatched = nativeWords.value.every(word => word.matched)
    && learningWords.value.every(word => word.matched)
  await emit('game-action', {
    success: isMatch,  // this is used by the server
    correct: isMatch,  // this is used by GameContainer
    concept_ids: [selectedPair.value.id, word.id],
    pending: !allMatched,  // game is not over yet
  })

  // Clear selection
  selectedPair.value = null
}

function getWordClass(word) {
  let classes = []
  if (word.wrong) classes.push('wrong')
  else if (word.matched) classes.push('matched')
  else if (selectedPair.value && selectedPair.value.id === word.id && selectedPair.value.type === word.type) {
    classes.push('selected')
  }
  if (props.compact) {
    classes.push('compact-word-card-xs')
  }
  return classes.join(' ')
}

function updateWordPositions() {
  console.log("updateWordPositions running")
  console.log("svgElement is", svgElement.value.getBoundingClientRect())
  const svgRect = svgElement.value.getBoundingClientRect()
  const nativeElements = document.querySelectorAll('.native-word')
  const learningElements = document.querySelectorAll('.learning-word')

  nativeElements.forEach(el => {
    const rect = el.getBoundingClientRect()
    console.log("rect is", rect)
    wordPositions.value[el.dataset.key] = {
      x: rect.left + rect.width - svgRect.left,
      y: rect.top + rect.height / 2 - svgRect.top
    }
  })

  learningElements.forEach(el => {
    const rect = el.getBoundingClientRect()
    wordPositions.value[el.dataset.key] = {
      x: rect.left - svgRect.left,
      y: rect.top + rect.height / 2 - svgRect.top
    }
  })
  updateMatchedWordPositions() // Update matchedWordPositions when word positions are updated
}

function playAudio(text) {
  if (!props.enable_audio) {
    return
  }
  const url = getBaseUrl(`/tts/?lang=${props.gameState.learning_language.name}&text=${text}&token=${user.token}`)
  audiomanager.setPlaybackSpeed(1)
  audiomanager.play(url)
}

onMounted(() => {
  nextTick(() => {
    updateWordPositions()
    window.addEventListener('resize', updateWordPositions)
  })
})

// Register your handler when component mounts
onMounted(() => {
  registerScrollHandler(updateWordPositions)
})

// Clean up when component unmounts
onUnmounted(() => {
  registerScrollHandler(null)
})

</script>

<template>
  <div class="matching-game-container border p-2 shadow rounded-4 mt-2 mb-0 py-1">
    <svg class="connection-lines" ref="svgElement">
      <line 
      v-for="(pos, index) in matchedWordPositions" 
      :key="index" 
      :x1="pos.native.x" 
      :y1="pos.native.y" 
      :x2="pos.learning.x" 
      :y2="pos.learning.y" 
      stroke="black" 
      stroke-width="2"
      />
      <circle 
      v-for="(pos, index) in matchedWordPositions" 
      :key="`circle-${index}`" 
      :cx="pos.learning.x" 
      :cy="pos.learning.y" 
      r="4" 
      fill="black"
      />
      <circle 
      v-for="(pos, index) in matchedWordPositions" 
      :key="`circle-${index}`" 
      :cx="pos.native.x" 
      :cy="pos.native.y" 
      r="4" 
      fill="black"
      />
    </svg>
    <div class="matching-grid">
      <!-- Native language column -->
      <div class="word-column">
        <h4 class="language-name">{{ t(`language.name.${props.gameState.native_language.id}`) }}</h4>
        <div 
          v-for="word in nativeWords" 
          :key="`native-${word.id}`"
          class="word-card native-word word-card-xs"
          :class="getWordClass(word)"
          :data-key="`native-${word.id}`"
          @click="selectWord(word)"
        >
          {{ word.text }}
        </div>
      </div>

      <!-- Learning language column -->
      <div class="word-column">
        <h4 class="language-name">{{ t(`language.name.${props.gameState.learning_language.id}`) }}</h4>
        <div 
          v-for="word in learningWords" 
          :key="`learning-${word.id}`"
          class="word-card learning-word word-card-xs"
          :class="getWordClass(word)"
          :data-key="`learning-${word.id}`"
          @click="selectWord(word)"
          v-html="word.annotation">
        </div>
      </div>
    </div>

  </div>
</template>

<style scoped>
.matching-game-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 1rem;
  position: relative;
  margin-bottom: 1rem;
}

.connection-lines {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.matching-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 0.5rem;
}

.compact-word-card-xs {
  /* slightly smaller padding than the other games to fit in the registration */
  padding: 0.3rem 1rem !important;
  margin: -0.2rem !important;
  font-size: 0.8rem !important;
}

@media (max-width: 576px) {
  .word-card-xs {
    padding: 0.5rem 1rem !important;
    margin: -0.2rem;
    font-size: 0.8rem !important;
  }
}
@media (max-width: 576px) {
  .word-card-xs {
    padding: 0.5rem !important;
    font-size: 0.8rem !important;
  }
}
.word-column {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.word-card {
  padding: 1rem;
  background-color: #f1f5f9;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
}

.word-card:hover {
  transform: translateY(-2px);
  border-color: #adb5bd;
}

.word-card.selected {
  background-color: #cfe2ff;
  border-color: var(--primary-color);
}

.word-card.matched {
  background-color: #d4edda;
  border-color: #28a745;
}

.word-card.wrong {
  background-color: #f8d7da;
  border-color: #dc3545;
}

.game-stats {
  text-align: center;
  font-size: 1.1rem;
}

h4.language-name {
  font-size: 1.1rem;
  margin-bottom: 0px;
}
.bi {
  color: black;
}
</style>
